import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  Lightbulb,
  Minus,
  RefreshCw,
  Sparkles,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import API from "../../lib/api";

const GRADE_STYLE = {
  A: "bg-green-100 text-green-700 border-green-200",
  B: "bg-lime-100 text-lime-700 border-lime-200",
  C: "bg-amber-100 text-amber-700 border-amber-200",
  D: "bg-orange-100 text-orange-700 border-orange-200",
  F: "bg-red-100 text-red-700 border-red-200",
};
const PRIORITY_STYLE = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-gray-100 text-gray-600",
};
// Hex equivalents of the grade colours for SVG charts.
const GRADE_HEX = {
  A: "#16A34A",
  B: "#65A30D",
  C: "#D97706",
  D: "#EA580C",
  F: "#DC2626",
};
const gradeColor = (g) => GRADE_HEX[g] || "#6C63FF";
const scoreColor = (s) =>
  !Number.isFinite(s) ? "#9CA3AF" : s >= 80 ? "#16A34A" : s >= 60 ? "#D97706" : "#DC2626";
const PRI_ORDER = { high: 0, medium: 1, low: 2 };

// The product runs only in India. Older AI reports were generated before the
// prompt was pinned to ₹ and contain "$" / "USD" / "dollars" — rewrite those
// at display time so the panel never shows non-INR currency to operators.
const toINR = (text) => {
  if (text == null) return text;
  return String(text)
    .replace(/\$/g, "₹")
    .replace(/\bUSD\b/g, "INR")
    .replace(/\bdollars?\b/gi, "rupees");
};

const RANGE_OPTIONS = [
  { value: "", label: "All time" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const fmtShort = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
    : "—";

// report_json is a MySQL JSON column — mysql2 usually returns it parsed, but
// guard for the string case too.
const parseReport = (r) => {
  if (!r) return {};
  if (typeof r === "object") return r;
  try {
    return JSON.parse(r);
  } catch {
    return {};
  }
};
const parseMetrics = (m) => {
  if (!m) return null;
  if (typeof m === "object") return m;
  try {
    return JSON.parse(m);
  } catch {
    return null;
  }
};

const TrendIcon = ({ trend }) => {
  if (trend === "up") return <TrendingUp className="h-3.5 w-3.5 text-green-600" />;
  if (trend === "down")
    return <TrendingDown className="h-3.5 w-3.5 text-red-600" />;
  return <Minus className="h-3.5 w-3.5 text-gray-400" />;
};

// ── Reusable inline-SVG / CSS charts (no dependencies) ─────────────────────

function RadialGauge({ score, grade, size = 84, stroke = 8 }) {
  const has = Number.isFinite(score);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = has ? Math.max(0, Math.min(100, score)) : 0;
  const col = gradeColor(grade);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF0F8" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={col}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct / 100)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="44%"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontWeight: 800, fontSize: size * 0.32, fill: col }}
      >
        {grade || "—"}
      </text>
      <text
        x="50%"
        y="70%"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: size * 0.14, fill: "#6B7280" }}
      >
        {has ? `${score}/100` : "—"}
      </text>
    </svg>
  );
}

function Sparkline({ data, color = "#6C63FF", height = 54, area = true, fmt = (v) => v, interactive = false }) {
  const pts = (data || []).filter((d) => Number.isFinite(d.value));
  const [hover, setHover] = useState(null);
  if (pts.length === 0)
    return <div className="text-[11px] text-[#9CA3AF] font-body py-4 text-center">No data</div>;

  const W = 100;
  const H = height;
  const n = pts.length;
  const x = (i) => (n === 1 ? W / 2 : (i / (n - 1)) * W);
  const vals = pts.map((p) => p.value);
  let min = Math.min(...vals);
  let max = Math.max(...vals);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const y = (v) => H - 3 - ((v - min) / (max - min)) * (H - 8);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(2)},${y(p.value).toFixed(2)}`).join(" ");
  const areaPath = `${line} L${x(n - 1).toFixed(2)},${H} L${x(0).toFixed(2)},${H} Z`;

  const onMove = (e) => {
    if (!interactive) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHover(Math.round(frac * (n - 1)));
  };

  return (
    <div className="relative" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        {area && <path d={areaPath} fill={color} opacity="0.12" />}
        <path
          d={line}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      {interactive && hover != null && pts[hover] && (
        <>
          <div
            className="absolute top-0 bottom-0 w-px bg-[#6C63FF]/30 pointer-events-none"
            style={{ left: `${x(hover)}%` }}
          />
          <div
            className="absolute w-2 h-2 rounded-full bg-white border-2 pointer-events-none -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${x(hover)}%`, top: `${(y(pts[hover].value) / H) * 100}%`, borderColor: color }}
          />
          <div
            className="absolute -translate-x-1/2 -translate-y-full -mt-1 pointer-events-none bg-[#1A1A2E] text-white text-[10px] font-body rounded px-1.5 py-0.5 whitespace-nowrap z-10"
            style={{ left: `${x(hover)}%`, top: `${(y(pts[hover].value) / H) * 100}%` }}
          >
            {pts[hover].label}: {fmt(pts[hover].value)}
          </div>
        </>
      )}
    </div>
  );
}

function Donut({ segments, size = 92, stroke = 13 }) {
  const segs = (segments || []).filter((s) => Number.isFinite(s.value) && s.value > 0);
  const total = segs.reduce((sum, s) => sum + s.value, 0);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF0F8" strokeWidth={stroke} />
      {total > 0 &&
        segs.map((s, i) => {
          const dash = (s.value / total) * c;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={-acc}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
          acc += dash;
          return el;
        })}
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: size * 0.2, fontWeight: 800, fill: "#1A1A2E" }}
      >
        {total}
      </text>
    </svg>
  );
}

const SectionLabel = ({ icon: Icon, color, children }) => (
  <p
    className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-heading font-bold mb-1.5"
    style={{ color }}
  >
    {Icon && <Icon className="h-3.5 w-3.5" />} {children}
  </p>
);

function MiniMetric({ title, data, color, fmt }) {
  const last = data.length ? data[data.length - 1].value : null;
  return (
    <div className="bg-[#F7FAFE] border border-[#E8E6F8] rounded-lg p-2.5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-heading font-bold text-[#6B7280]">
          {title}
        </span>
        <span className="text-xs font-heading font-bold text-[#1A1A2E]">
          {last == null ? "—" : fmt ? fmt(last) : last}
        </span>
      </div>
      <Sparkline data={data} color={color} height={40} fmt={fmt} interactive />
    </div>
  );
}

const DEVICE_HEX = { mobile: "#6C63FF", desktop: "#0EA5E9", tablet: "#F59E0B" };
const BRAND_HEX = { myca: "#6C63FF", filemytax: "#16A34A" };

function DonutBlock({ title, segments }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (total === 0) return null;
  return (
    <div className="bg-[#F7FAFE] border border-[#E8E6F8] rounded-lg p-3 flex items-center gap-3">
      <Donut segments={segments} size={76} stroke={11} />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-[#6B7280] mb-1">
          {title}
        </p>
        <ul className="space-y-0.5">
          {segments
            .filter((s) => s.value > 0)
            .map((s, i) => (
              <li key={i} className="flex items-center gap-1.5 text-[11px] font-body text-[#4A4A60]">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                <span className="capitalize truncate">{s.label}</span>
                <span className="ml-auto font-medium text-[#1A1A2E]">{s.value}</span>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
}

// ── Per-report infographic card ────────────────────────────────────────────
function ReportCard({ row }) {
  const [open, setOpen] = useState(false);
  const report = parseReport(row.report_json);
  const metrics = parseMetrics(row.metrics_json);
  const grade = row.overall_grade || report.overall_grade || "—";
  const rawScore = row.overall_score ?? report.overall_score;
  const score = Number.isFinite(rawScore) ? rawScore : null;

  const areas = Array.isArray(report.areas) ? report.areas : [];
  const highlights = Array.isArray(report.highlights) ? report.highlights : [];
  const regressions = Array.isArray(report.regressions) ? report.regressions : [];
  const recs = Array.isArray(report.recommendations)
    ? [...report.recommendations].sort(
        (a, b) => (PRI_ORDER[a.priority] ?? 3) - (PRI_ORDER[b.priority] ?? 3),
      )
    : [];

  const series = (key, mult = 1) =>
    metrics && Array.isArray(metrics.daily_series)
      ? metrics.daily_series.map((d) => ({
          label: fmtShort(d.date),
          value: (Number(d[key]) || 0) * mult,
        }))
      : [];

  const deviceSegs = (metrics?.device_split || []).map((d) => ({
    label: d.device,
    value: Number(d.visitors) || 0,
    color: DEVICE_HEX[d.device] || "#9CA3AF",
  }));
  const brandSegs = (metrics?.brand_split || []).map((b) => ({
    label: b.brand,
    value: Number(b.visitors) || 0,
    color: BRAND_HEX[b.brand] || "#9CA3AF",
  }));
  const topPaths = (metrics?.top_paths || []).slice(0, 6);
  const maxViews = topPaths.reduce((m, p) => Math.max(m, Number(p.views) || 0), 0) || 1;

  const Pill = ({ count, label, color }) =>
    count > 0 ? (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-body font-semibold px-1.5 py-0.5 rounded-full"
        style={{ color, background: `${color}1A` }}
      >
        {count} {label}
      </span>
    ) : null;

  return (
    <div className="bg-white border border-[#E8E6F8] rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#F5F4FB] transition-colors"
      >
        <RadialGauge score={score} grade={grade} size={52} stroke={6} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-heading font-bold text-[#1A1A2E]">{fmtDate(row.report_date)}</p>
          <p className="text-xs text-[#6B7280] font-body mt-0.5 line-clamp-2">
            {toINR(row.summary || report.summary) || "—"}
          </p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <Pill count={highlights.length} label="highlights" color="#16A34A" />
            <Pill count={regressions.length} label="regressions" color="#DC2626" />
            <Pill count={recs.length} label="actions" color="#6C63FF" />
          </div>
        </div>
        <div className="text-[#9CA3AF] flex-shrink-0">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-0 border-t border-[#F0EEF9] space-y-4">
          {report.summary && (
            <p className="text-sm font-body text-[#1A1A2E] leading-relaxed pt-3">
              {toINR(report.summary)}
            </p>
          )}

          {/* Area scores as graded bars */}
          {areas.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-2.5">
              {areas.map((a, i) => {
                const s = Number.isFinite(a.score) ? a.score : 0;
                const col = scoreColor(a.score);
                return (
                  <div key={i} className="bg-[#F7FAFE] border border-[#E8E6F8] rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs font-heading font-semibold text-[#1A1A2E]">{a.name}</span>
                      <span className="inline-flex items-center gap-1 text-xs font-body" style={{ color: col }}>
                        <TrendIcon trend={a.trend} />
                        {Number.isFinite(a.score) ? a.score : "—"}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-[#EEF0F8] overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${Math.max(0, Math.min(100, s))}%`, background: col }}
                      />
                    </div>
                    {a.note && <p className="text-[11px] text-[#6B7280] font-body mt-1">{toINR(a.note)}</p>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Raw daily-metric charts (from metrics_json) */}
          {metrics && Array.isArray(metrics.daily_series) && metrics.daily_series.length > 0 && (
            <div>
              <SectionLabel color="#6C63FF">
                Traffic &amp; Revenue · last {metrics.daily_series.length} days
              </SectionLabel>
              <div className="grid sm:grid-cols-2 gap-2.5">
                <MiniMetric title="Pageviews" data={series("pageviews")} color="#6C63FF" />
                <MiniMetric title="Unique Visitors" data={series("unique_visitors")} color="#0EA5E9" />
                <MiniMetric
                  title="Conversion %"
                  data={series("conversion_rate_pct")}
                  color="#16A34A"
                  fmt={(v) => `${v}%`}
                />
                <MiniMetric
                  title="Revenue"
                  data={series("revenue")}
                  color="#F59E0B"
                  fmt={(v) => `₹${Number(v).toLocaleString("en-IN")}`}
                />
              </div>
              {(deviceSegs.length > 0 || brandSegs.length > 0) && (
                <div className="grid sm:grid-cols-2 gap-2.5 mt-2.5">
                  <DonutBlock title="Devices" segments={deviceSegs} />
                  <DonutBlock title="Brands" segments={brandSegs} />
                </div>
              )}
              {topPaths.length > 0 && (
                <div className="bg-[#F7FAFE] border border-[#E8E6F8] rounded-lg p-3 mt-2.5">
                  <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-[#6B7280] mb-2">
                    Top Pages
                  </p>
                  <ul className="space-y-1.5">
                    {topPaths.map((p, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="text-[11px] font-mono text-[#4A4A60] w-40 truncate flex-shrink-0">
                          {p.path}
                        </span>
                        <div className="flex-1 h-2 rounded-full bg-[#EEF0F8] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-[#6C63FF]"
                            style={{ width: `${((Number(p.views) || 0) / maxViews) * 100}%` }}
                          />
                        </div>
                        <span className="text-[11px] font-body text-[#1A1A2E] w-10 text-right">
                          {p.views}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Highlights */}
          {highlights.length > 0 && (
            <div className="bg-green-50/60 border border-green-100 rounded-lg p-3">
              <SectionLabel icon={CheckCircle2} color="#15803D">
                Highlights · {highlights.length}
              </SectionLabel>
              <ul className="space-y-1">
                {highlights.map((h, i) => (
                  <li key={i} className="text-xs font-body text-[#1A1A2E] flex gap-1.5">
                    <span className="text-green-600">•</span> {toINR(h)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Regressions */}
          {regressions.length > 0 && (
            <div className="bg-red-50/60 border border-red-100 rounded-lg p-3">
              <SectionLabel icon={AlertTriangle} color="#B91C1C">
                Regressions · {regressions.length}
              </SectionLabel>
              <ul className="space-y-1">
                {regressions.map((r, i) => (
                  <li key={i} className="text-xs font-body text-[#1A1A2E] flex gap-1.5">
                    <span className="text-red-600">•</span> {toINR(r)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {recs.length > 0 && (
            <div>
              <SectionLabel icon={Lightbulb} color="#6C63FF">
                Recommendations · {recs.length}
              </SectionLabel>
              <ul className="space-y-1.5">
                {recs.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span
                      className={`text-[9px] font-body font-bold uppercase px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0 ${
                        PRIORITY_STYLE[rec.priority] || PRIORITY_STYLE.low
                      }`}
                    >
                      {rec.priority}
                    </span>
                    <span className="text-xs font-body text-[#1A1A2E]">{toINR(rec.action)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-[10px] text-[#9CA3AF] font-body">
            Generated by {row.model || "AI"} ·{" "}
            {row.created_at ? new Date(row.created_at).toLocaleString("en-IN") : ""}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Overview infographic (computed from the fetched rows) ──────────────────
function Overview({ rows }) {
  const scored = rows
    .map((r) => ({
      date: r.report_date,
      score: Number.isFinite(r.overall_score)
        ? r.overall_score
        : parseReport(r.report_json).overall_score,
      grade: r.overall_grade || parseReport(r.report_json).overall_grade || "—",
    }))
    .filter((r) => Number.isFinite(r.score));

  const latest = rows[0]
    ? {
        score: Number.isFinite(rows[0].overall_score)
          ? rows[0].overall_score
          : parseReport(rows[0].report_json).overall_score,
        grade: rows[0].overall_grade || parseReport(rows[0].report_json).overall_grade || "—",
        date: rows[0].report_date,
      }
    : null;

  const avg = scored.length
    ? Math.round(scored.reduce((s, r) => s + r.score, 0) / scored.length)
    : null;
  const best = scored.reduce((b, r) => (!b || r.score > b.score ? r : b), null);
  const worst = scored.reduce((w, r) => (!w || r.score < w.score ? r : w), null);

  // Oldest → newest for the time-series sparkline.
  const trend = [...scored]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((r) => ({ label: fmtShort(r.date), value: r.score }));

  const gradeCounts = { A: 0, B: 0, C: 0, D: 0, F: 0 };
  rows.forEach((r) => {
    const g = r.overall_grade || parseReport(r.report_json).overall_grade;
    if (g && gradeCounts[g] != null) gradeCounts[g] += 1;
  });
  const gradeSegs = ["A", "B", "C", "D", "F"].map((g) => ({
    label: g,
    value: gradeCounts[g],
    color: GRADE_HEX[g],
  }));

  const Tile = ({ label, value, sub }) => (
    <div className="bg-[#F7FAFE] border border-[#E8E6F8] rounded-xl px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-[#9CA3AF]">{label}</p>
      <p className="text-lg font-heading font-black text-[#1A1A2E] leading-tight mt-0.5">{value}</p>
      {sub && <p className="text-[11px] text-[#6B7280] font-body">{sub}</p>}
    </div>
  );

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* Latest gauge + KPI tiles */}
      <div className="bg-white border border-[#E8E6F8] rounded-2xl p-4">
        <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-[#9CA3AF] mb-3">
          Latest report
        </p>
        <div className="flex items-center gap-4">
          <RadialGauge score={latest?.score ?? null} grade={latest?.grade ?? "—"} size={92} />
          <div className="grid grid-cols-2 gap-2 flex-1">
            <Tile label="Average" value={avg == null ? "—" : `${avg}`} sub="score" />
            <Tile label="Reports" value={rows.length} sub="in range" />
            <Tile
              label="Best"
              value={best ? best.grade : "—"}
              sub={best ? `${best.score} · ${fmtShort(best.date)}` : ""}
            />
            <Tile
              label="Worst"
              value={worst ? worst.grade : "—"}
              sub={worst ? `${worst.score} · ${fmtShort(worst.date)}` : ""}
            />
          </div>
        </div>
      </div>

      {/* Score over time */}
      <div className="bg-white border border-[#E8E6F8] rounded-2xl p-4">
        <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-[#9CA3AF] mb-2">
          Score over time
        </p>
        {trend.length > 0 ? (
          <Sparkline data={trend} color="#6C63FF" height={96} interactive />
        ) : (
          <p className="text-xs text-[#9CA3AF] font-body py-8 text-center">No scored reports yet.</p>
        )}
      </div>

      {/* Grade distribution */}
      <div className="bg-white border border-[#E8E6F8] rounded-2xl p-4">
        <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-[#9CA3AF] mb-2">
          Grade distribution
        </p>
        <div className="flex items-center gap-4">
          <Donut segments={gradeSegs} size={96} />
          <ul className="space-y-1">
            {gradeSegs.map((s) => (
              <li key={s.label} className="flex items-center gap-2 text-xs font-body text-[#4A4A60]">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
                <span className="font-heading font-bold w-3">{s.label}</span>
                <span className="ml-auto font-medium text-[#1A1A2E]">{s.value}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function AiReportPanel() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = {};
    if (range) {
      const d = new Date();
      d.setDate(d.getDate() - (range === "7d" ? 7 : 30));
      params.from = d.toISOString().slice(0, 10);
    }
    try {
      const { data } = await API.get("/admin/ai-perf-reports", { params });
      setReports(data.reports || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load reports");
    }
    setLoading(false);
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = () => {
    // Reports are already in state — flatten the structured JSON into a CSV
    // that preserves the human-readable parts (highlights / regressions /
    // recommendations joined with " | " so each report stays on one row).
    const headers = [
      "Report Date", "Grade", "Score", "Summary",
      "Highlights", "Regressions", "Recommendations",
      "Areas", "Model", "Generated At",
    ];

    // RFC 4180 quoting + INR rewrite (legacy reports may still contain "$").
    const esc = (v) => {
      if (v == null) return "";
      const s = toINR(String(v));
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const joinList = (arr) =>
      Array.isArray(arr) ? arr.map(toINR).join(" | ") : "";

    const formatRecs = (recs) =>
      Array.isArray(recs)
        ? recs.map((r) => `[${(r.priority || "").toUpperCase()}] ${toINR(r.action || "")}`).join(" | ")
        : "";

    const formatAreas = (areas) =>
      Array.isArray(areas)
        ? areas.map((a) => `${a.name}: ${a.score}${a.trend ? ` (${a.trend})` : ""}`).join(" | ")
        : "";

    const rows = reports.map((row) => {
      const r = parseReport(row.report_json);
      return [
        row.report_date,
        row.overall_grade ?? r.overall_grade,
        row.overall_score ?? r.overall_score,
        row.summary || r.summary,
        joinList(r.highlights),
        joinList(r.regressions),
        formatRecs(r.recommendations),
        formatAreas(r.areas),
        row.model,
        row.created_at ? new Date(row.created_at).toLocaleString("en-IN") : "",
      ].map(esc).join(",");
    });

    const csv = [headers.map(esc).join(","), ...rows].join("\r\n");
    // UTF-8 BOM so Excel renders ₹ and Indian names cleanly.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    const suffix = range ? `-${range}` : "";
    a.href = url;
    a.download = `ai-reports${suffix}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-[#6C63FF]/10 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-[#6C63FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-heading font-bold text-[#1A1A2E]">
            AI Performance Reports
          </h2>
          <p className="text-xs text-[#6B7280] font-body">
            Daily AI analysis of your web app's performance, newest first.
          </p>
        </div>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          {RANGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          onClick={handleExport}
          disabled={loading || reports.length === 0}
          title="Download all visible reports as CSV"
          className="inline-flex items-center gap-1.5 text-sm font-body font-medium text-white bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-60 disabled:cursor-not-allowed rounded-lg px-3 py-2 transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-sm font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#6C63FF]/5 rounded-lg px-3 py-2 transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="p-8 text-center bg-red-50 border border-red-200 rounded-2xl">
          <AlertTriangle className="h-5 w-5 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-600 font-body">{error}</p>
          <button
            onClick={load}
            className="mt-2 text-xs text-[#6C63FF] underline font-body"
          >
            Retry
          </button>
        </div>
      ) : reports.length === 0 ? (
        <div className="py-16 text-center bg-white border border-[#E8E6F8] rounded-2xl">
          <Sparkles className="h-10 w-10 mx-auto text-[#8E97DD] mb-3" />
          <p className="font-heading font-semibold text-[#1A1A2E]">
            No reports yet
          </p>
          <p className="text-sm text-[#6B7280] font-body mt-1 max-w-md mx-auto">
            The nightly job generates one report per day. Once it runs (or you
            run it manually), reports appear here.
          </p>
        </div>
      ) : (
        <>
          <Overview rows={reports} />
          <div className="space-y-3">
            {reports.map((row) => (
              <ReportCard key={row.id} row={row} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
